import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { saveAs } from 'file-saver';
import { forkJoin, from, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import {
  ESignTemplateFieldForm,
  ESignTemplateForm
} from 'src/app/classes/form-groups/esign-template-form';
import { Company, ESignProvider, ESignTemplate } from 'src/app/classes/models';
import { File as FileModel } from 'src/app/classes/models/file';
import { CompanyService } from 'src/app/services/company.service';
import { ESignService } from 'src/app/services/esign.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { esignTemplatesService } from 'src/app/shared/vive-api/esign-templates.service';
import {
  EsignTemplatesFieldMappingRequestDtoFieldTypeEnum,
  EsignTemplatesFieldMappingRequestDtoSourceEnum,
  EsignTemplatesFileVersionResponseDto,
  FilesUploadToS3RequestDto
} from 'src/app/shared/vive-api/vive-api-models';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';
import { ViveApiError } from 'src/app/shared/vive-api/vive-api.error';

/** A mapping row as it goes over the wire, with every view-state control removed. */
type SubmittableField = Omit<
  ReturnType<ESignTemplateFieldForm['getRawValue']>,
  'isInPdf' | 'wasSuggested' | 'options' | 'takesNoValue' | 'optionLabels'
> & { optionLabels: Record<string, string> | null };

@Component({
  selector: 'app-add-edit-esign-template-modal',
  templateUrl: './add-edit-esign-template-modal.component.html',
  styleUrl: './add-edit-esign-template-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditEsignTemplateModalComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private activeModal: NgbActiveModal,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private CompanyService: CompanyService,
    private ESignService: ESignService
  ) {}

  @Input() ESignTemplate?: ESignTemplate;

  ESignTemplateForm: ESignTemplateForm = new ESignTemplateForm();
  Companies?: Company[];
  ESignProviders?: ESignProvider[];
  templateTypes = [
    { value: 'Vendor Agreement', display: 'Vendor Agreement' },
    { value: 'W9', display: 'W9' },
    { value: 'Vendor Agreement & W9', display: 'Vendor Agreement & W9' }
  ];

  Files: FileModel[] = [];
  File: File | null = null;

  /**
   * The picklist, split the three ways an admin actually thinks about a field:
   * VIVE already knows it, it is a fixed string, or only the vendor can supply
   * it. A picklist and never a text box — a typed source is a typo that prints
   * blank on an executed contract with nothing anywhere saying so.
   */
  readonly sourceGroups: {
    label: string;
    options: {
      value: EsignTemplatesFieldMappingRequestDtoSourceEnum;
      display: string;
      /** Prompts for the extra argument this source needs. */
      needsParam?: 'digit' | 'companyType';
      isPii?: boolean;
    }[];
  }[] = [
    {
      label: 'VIVE already knows this',
      options: [
        { value: 'CLIENT_NAME', display: 'Client — legal name' },
        { value: 'CLIENT_STREET_ADDRESS', display: 'Client — street address' },
        { value: 'VENDOR_NAME', display: 'Vendor — legal name' },
        { value: 'VENDOR_DBA', display: 'Vendor — DBA' },
        {
          value: 'VENDOR_MAILING_ADDRESS',
          display: 'Vendor — mailing address'
        },
        {
          value: 'VENDOR_MAILING_ADDRESS_LINE1',
          display: 'Vendor — mailing address line 1'
        },
        {
          value: 'VENDOR_MAILING_CITY_STATE_ZIP',
          display: 'Vendor — city, state, ZIP'
        },
        { value: 'VENDOR_TAX_CLASS', display: 'Vendor — tax class' },
        {
          value: 'VENDOR_COMPANY_TYPE_IS',
          display: 'Vendor — company type is…',
          needsParam: 'companyType'
        },
        {
          value: 'VENDOR_SSN_DIGIT',
          display: 'Vendor — SSN digit',
          needsParam: 'digit',
          isPii: true
        },
        {
          value: 'VENDOR_EIN_DIGIT',
          display: 'Vendor — EIN digit',
          needsParam: 'digit',
          isPii: true
        },
        {
          value: 'PRIMARY_COMPLIANCE_CONTACT_NAME',
          display: 'Primary compliance contact — name'
        },
        { value: 'SIGNER_NAME', display: 'Signer — name' },
        { value: 'SIGNED_DATE', display: 'Signed — date' },
        { value: 'SIGNED_MONTH_DAY', display: 'Signed — month and day' },
        { value: 'SIGNED_YEAR_LAST_TWO', display: 'Signed — year, last two' }
      ]
    },
    {
      label: 'A fixed value',
      options: [
        { value: 'STATIC', display: 'Always this text…' },
        { value: 'BLANK', display: 'Leave blank — nothing fills this' }
      ]
    },
    {
      label: 'Only the vendor can supply it',
      options: [{ value: 'VENDOR_INPUT', display: 'Ask the vendor' }]
    }
  ];

  /**
   * Field names that read like bank or tax identifiers. Used for a WARNING
   * only — VIVE cannot stop a client collecting this on their own paper, and a
   * hard block would push it somewhere with no audit trail at all. The warning
   * exists so the choice is made knowingly.
   */
  private readonly piiFieldNamePattern =
    /(routing|account|bank|ssn|social|ein|tax\s*id|taxid)/i;

  /**
   * The validation an admin can ask for, as the three shapes a vendor answer
   * actually takes. Stored as the regex the backend applies, so the picklist is
   * a vocabulary rather than a free-text pattern box — a hand-written regex is
   * something nobody can review and the vendor meets as an unexplained refusal.
   */
  readonly vendorInputValidationRules: {
    value: null | string;
    display: string;
  }[] = [
    { value: null, display: 'Any text' },
    { value: '^[0-9]+$', display: 'Numbers only' },
    { value: '^\\d{4}-\\d{2}-\\d{2}$', display: 'Date (YYYY-MM-DD)' }
  ];

  /**
   * The field types a vendor answers by TYPING, and so the only ones a length
   * limit or a validation pattern can describe.
   *
   * A checkbox answers with one agreed value or nothing, and a radio group,
   * dropdown or list box answers with one of the PDF's own export values — the
   * file already fixes what is acceptable, so a pattern could only ever refuse
   * an answer the document itself offered.
   */
  private readonly typedAnswerFieldTypes: EsignTemplatesFieldMappingRequestDtoFieldTypeEnum[] =
    ['text', 'multilineText', 'date'];

  acroFormFieldsLoading = false;
  acroFormFieldsError: string | null = null;
  resignImpactCount: number | null = null;
  currentTemplateFile: EsignTemplatesFileVersionResponseDto | null = null;
  previousTemplateFileVersions: EsignTemplatesFileVersionResponseDto[] = [];
  isPreviousVersionsOpen = false;
  isDownloadingTemplateFile = false;

  /**
   * Whether the template already had a saved mapping row when its fields were
   * read, and the payload rows exactly as read. Together they decide whether
   * a save writes the mapping at all. See `willSendFields`.
   */
  private hadSavedMapping = false;
  private loadedFieldsSnapshot: string | null = null;

  public get fields(): FormArray<ESignTemplateFieldForm> {
    return this.ESignTemplateForm.controls.fields;
  }

  /**
   * A W9 template can never ask the vendor to fill anything in — the form is
   * the IRS's and its fields are prescribed. The backend refuses it in storage
   * and again in the service; hiding the option is so an admin never picks
   * something that was always going to be rejected.
   */
  public get sourceGroupsForTemplate(): typeof this.sourceGroups {
    if (this.ESignTemplateForm.controls.templateType.value !== 'W9') {
      return this.sourceGroups;
    }

    return this.sourceGroups
      .map((group) => ({
        ...group,
        options: group.options.filter(
          (option) => option.value !== 'VENDOR_INPUT'
        )
      }))
      .filter((group) => group.options.length > 0);
  }

  /**
   * Fields an admin actually decided. A widget that takes no value arrives as
   * `BLANK` and isn't counted, because there was never a decision to make.
   */
  public get mappedFieldCount(): number {
    return this.fields.controls.filter(
      (field) =>
        field.controls.isInPdf.value &&
        field.controls.source.value &&
        !field.controls.takesNoValue.value
    ).length;
  }

  /**
   * Whether saving writes the field mapping, or leaves it exactly as it is.
   *
   * vive-v2 replaces the whole mapping whenever `fields` is sent, and a
   * template that had no mapping moves off NULL on its first one. That puts
   * every signed vendor of the client on the re-sign list, even when every row
   * matches VIVE's standard handling. So a template with no saved mapping only
   * sends `fields` when the admin actually changed a row. A details-only edit,
   * or a file swap with the rows untouched, keeps it on standard handling.
   *
   * A template that already has a mapping always sends, as it always has.
   * Identical rows give an identical fingerprint, and a changed row should
   * move it.
   */
  public get willSendFields(): boolean {
    return (
      this.fields.length > 0 &&
      (this.hadSavedMapping ||
        JSON.stringify(this.submittableFields()) !== this.loadedFieldsSnapshot)
    );
  }

  /** Prefilled from what VIVE already stamps, and not yet changed by hand. */
  public get autoMappedFieldCount(): number {
    return this.fields.controls.filter(
      (field) =>
        field.controls.wasSuggested.value && field.controls.source.value
    ).length;
  }

  public get unmappedFieldCount(): number {
    return this.fields.controls.filter(
      (field) => field.controls.isInPdf.value && !field.controls.source.value
    ).length;
  }

  /** Mapping rows naming a field the uploaded PDF no longer has. */
  public get missingFieldCount(): number {
    return this.fields.controls.filter((field) => !field.controls.isInPdf.value)
      .length;
  }

  /**
   * Fields whose name or chosen source looks like bank or tax data. Warns,
   * never blocks — see `piiFieldNamePattern`.
   */
  public get piiFieldNames(): string[] {
    return this.fields.controls
      .filter((field) => {
        const source = field.controls.source.value;
        const sourceIsPii = this.sourceGroups.some((group) =>
          group.options.some(
            (option) => option.value === source && option.isPii
          )
        );

        return (
          sourceIsPii ||
          this.piiFieldNamePattern.test(field.controls.pdfFieldName.value)
        );
      })
      .map((field) => field.controls.pdfFieldName.value);
  }

  /**
   * Vendor-facing choice fields still carrying an unnamed option.
   *
   * Warns, never blocks — a missing label is cosmetic where a missing source
   * decides what lands on a contract, and an unrelated edit should not be held
   * hostage to one. But it is worth saying out loud: the backend falls back to
   * the PDF's own export value, so the vendor is asked to pick between
   * `Choice1` and `Choice2` on a document they are about to sign.
   */
  public get unlabelledOptionFieldNames(): string[] {
    return this.fields.controls
      .filter(
        (field) =>
          field.controls.source.value === 'VENDOR_INPUT' &&
          field.controls.options.value.some(
            (option) =>
              !field.controls.optionLabels.controls[option]?.value.trim()
          )
      )
      .map((field) => field.controls.pdfFieldName.value);
  }

  /**
   * Whether this field is one a vendor types into, and so whether a max length
   * and a validation pattern have anything to constrain.
   */
  public takesTypedAnswer(
    fieldType: EsignTemplatesFieldMappingRequestDtoFieldTypeEnum
  ): boolean {
    return this.typedAnswerFieldTypes.includes(fieldType);
  }

  public needsParamFor(
    source: null | EsignTemplatesFieldMappingRequestDtoSourceEnum
  ): 'digit' | 'companyType' | null {
    for (const group of this.sourceGroups) {
      const option = group.options.find((entry) => entry.value === source);

      if (option) {
        return option.needsParam ?? null;
      }
    }

    return null;
  }

  public get isViveProvider(): boolean {
    const providerId = this.ESignTemplateForm.controls.ESignProviderId.value;
    return (
      this.ESignProviders?.find((p) => p.id === providerId)?.name === 'VIVE'
    );
  }

  public onESignProviderChange(): void {
    const providerId = this.ESignTemplateForm.controls.ESignProviderId.value;
    const provider = this.ESignProviders?.find((p) => p.id === providerId);

    if (provider?.name === 'VIVE') {
      if (!this.ESignTemplateForm.controls.TemplateId.value) {
        this.ESignTemplateForm.controls.TemplateId.setValue(
          crypto.randomUUID()
        );
      }
      this.ESignTemplateForm.controls.TemplateId.disable();
      this.ESignTemplateForm.addControl(
        'TemplateFile',
        new FormGroup({
          filename: new FormControl<undefined | string>(undefined, {
            nonNullable: true
          }),
          filetype: new FormControl<undefined | string>(undefined, {
            nonNullable: true
          }),
          FileContent: new FormGroup({
            filecontents: new FormControl<undefined | string>(undefined, {
              nonNullable: true
            })
          })
        })
      );
    } else {
      this.ESignTemplateForm.controls.TemplateId.setValue(undefined);
      this.ESignTemplateForm.controls.TemplateId.enable();
      this.ESignTemplateForm.removeControl('TemplateFile');
    }
  }

  fileUpload({ data: File }: { data: File }) {
    const { name: filename, size: filesize, type: filetype } = File;

    const supportedFileTypes: File['type'][] = ['pdf', 'application/pdf'];

    if (this.Files.length >= 1) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Can only upload one document'
      );
    }

    if (!supportedFileTypes.includes(filetype)) {
      throw this.ToastService.addErrorToast(
        'Error',
        'File type not supported.'
      );
    }

    from(File.arrayBuffer()).subscribe({
      next: (FileArrayBuffer) => {
        const FileString =
          this.UtilityService.convertArrayBufferToBase64EncodedString(
            FileArrayBuffer
          );

        if (FileString) {
          const templateFile: FileModel = {
            filename,
            filesize,
            filetype,
            FileContent: { filecontents: FileString }
          };

          this.ESignTemplateForm.controls.TemplateFile?.patchValue(
            templateFile
          );
          this.Files.push(templateFile);

          // Against the candidate, not the stored file: the admin needs to see
          // which mappings this new PDF would strand while the swap is still
          // cancellable.
          //
          // Rebuilt rather than passed whole — the app's File model has these
          // optional and the request type does not, and `filesize` is not
          // something this endpoint wants.
          this.loadAcroFormFields({
            filename,
            filetype,
            FileContent: { filecontents: FileString }
          });
          this._cdr.markForCheck();
        }
      }
    });
  }

  /**
   * Reads the template's AcroForm fields and rebuilds the mapping table.
   *
   * Two ways in, both on a saved template:
   *
   *  - no new file: read its stored PDF and show the mapping it already has;
   *  - a file the admin just picked: compare the existing mapping against the
   *    CANDIDATE, so a renamed field is found while the swap is still
   *    cancellable. Replacing a VIVE template file moves the checksum and asks
   *    every vendor holding a signature to sign again.
   *
   * Never on Add. The Add dialog hides the table ("Save this template first,
   * then reopen it to map the fields"), so rows read here would sit where no
   * admin can see them. Every VIVE PDF carries a signature widget, and that
   * hidden row disabled Save, so no VIVE template could be added at all. A new
   * template is created on standard handling and mapped on reopen.
   */
  public loadAcroFormFields(candidateFile?: FilesUploadToS3RequestDto): void {
    const esignTemplateId = this.ESignTemplateForm.controls.id.value;

    if (!esignTemplateId) {
      return;
    }

    this.acroFormFieldsLoading = true;
    this.acroFormFieldsError = null;

    const options = { requestTimeout: LOAD_BALANCER_TIMEOUT_MS };
    const request = candidateFile
      ? esignTemplatesService.findAcroFormFieldsForCandidateFile({
          pathParams: { esignTemplateId },
          data: { TemplateFile: candidateFile },
          options
        })
      : esignTemplatesService.findAcroFormFields({
          pathParams: { esignTemplateId },
          options
        });

    from(request).subscribe({
      next: (acroFormFields) => {
        this.fields.clear();

        for (const acroFormField of acroFormFields) {
          // A name VIVE's standard handling already fills arrives prefilled, so
          // the admin confirms it rather than picking from nineteen options for
          // a field that was never in question. Only the client's own fields
          // are left empty, and those are the ones that actually need a
          // decision.
          //
          // The suggestion is only ever offered where nothing is mapped — the
          // backend withholds it once a row exists, so a saved decision cannot
          // be overwritten by the default that preceded it.
          const wasSuggested =
            !acroFormField.source && !!acroFormField.suggestedSource;

          // A widget nothing is ever written into, read off the RAW type
          // before the fallback below erases the difference. A signature is
          // added when the vendor signs, and prefill skips it. A null type is
          // a widget the backend doesn't recognize, such as a push button. A
          // row for a field the PDF no longer has is never one: it keeps its
          // "Not in PDF" handling and its source picker.
          const takesNoValue =
            acroFormField.isInPdf &&
            (acroFormField.fieldType === null ||
              acroFormField.fieldType === 'signature');

          // A field missing from the PDF reports the type its MAPPING row
          // recorded; only a widget that takes no value comes back null, and
          // text is the sane thing to offer for one.
          const fieldType = acroFormField.fieldType ?? 'text';

          this.fields.push(
            new ESignTemplateFieldForm({
              pdfFieldName: acroFormField.pdfFieldName,
              fieldType,
              // A no-value widget with nothing saved defaults to `BLANK`, the
              // exact row an admin produced before by choosing Leave blank, so
              // the mapping fingerprint is what it would have been. A saved
              // source, BLANK or not, is kept as it is.
              source:
                acroFormField.source ??
                acroFormField.suggestedSource ??
                (takesNoValue ? 'BLANK' : null),
              // The SAVED value first, the suggestion only as a fallback.
              // Reading the suggestion alone meant reopening a template and
              // saving it again wrote every one of these back as a default —
              // clearing a static value, dropping the positional argument that
              // fills a W-9 tax-id box, and resetting a vendor-facing
              // question's order, required flag, pattern and length. Nothing
              // said it had happened, and VPDW-4842 renders the vendor's form
              // from exactly those four.
              //
              // The two cannot both be set: the backend withholds a suggestion
              // once a mapping row exists, so this reads as "what was saved,
              // else what we would suggest" with no case where they compete.
              sourceParam:
                acroFormField.sourceParam ??
                acroFormField.suggestedSourceParam ??
                null,
              staticValue:
                acroFormField.staticValue ??
                acroFormField.suggestedStaticValue ??
                null,
              displayLabel: acroFormField.displayLabel ?? null,
              // `?? undefined` so the form's own default applies for a field
              // with no mapping row; `?? 0` / `?? false` here would say the
              // same thing twice and drift from it later.
              displayOrder: acroFormField.displayOrder ?? undefined,
              isRequired: acroFormField.isRequired ?? undefined,
              // Dropped outright for anything a vendor does not type into.
              // Replacing a template file can turn a text field into a
              // checkbox under a mapping row that still carries both, and the
              // row is rebuilt from the server on every load — so clearing
              // here is the only moment `fieldType` and these two can disagree,
              // and a length limit stored against a checkbox is a rule no
              // answer could ever satisfy.
              validationRegex: this.takesTypedAnswer(fieldType)
                ? (acroFormField.validationRegex ?? null)
                : null,
              maxLength: this.takesTypedAnswer(fieldType)
                ? (acroFormField.maxLength ?? null)
                : null,
              // The option set is the FILE's, read fresh on every request; only
              // the labels were saved. A label whose export value the PDF has
              // since dropped therefore has no control to render into, which is
              // the intended outcome — the vendor cannot be offered a choice
              // the document can no longer record.
              options: acroFormField.options,
              optionLabels: acroFormField.optionLabels,
              isInPdf: acroFormField.isInPdf,
              wasSuggested,
              takesNoValue
            })
          );
        }

        // Re-taken on every read, a candidate file's included, because the
        // table is rebuilt from scratch each time. See `willSendFields`.
        this.hadSavedMapping = acroFormFields.some(
          (acroFormField) => acroFormField.ESignTemplateFieldId !== null
        );
        this.loadedFieldsSnapshot = JSON.stringify(this.submittableFields());

        this.acroFormFieldsLoading = false;
        this._cdr.markForCheck();
      },
      error: (error: unknown) => {
        const { message } =
          error instanceof ViveApiError
            ? ((error.cause ?? {}) as { message?: unknown })
            : {};
        this.acroFormFieldsLoading = false;
        this.acroFormFieldsError =
          typeof message === 'string' && message.trim()
            ? message
            : 'Could not read this PDF’s form fields. Check the file and try again.';
        this._cdr.markForCheck();
      }
    });
  }

  public loadTemplateFileVersions(): void {
    const esignTemplateId = this.ESignTemplateForm.controls.id.value;

    if (!esignTemplateId) {
      this.currentTemplateFile = null;
      this.previousTemplateFileVersions = [];
      return;
    }

    from(
      esignTemplatesService.findTemplateFileVersions({
        pathParams: { esignTemplateId }
      })
    )
      .pipe(catchError(() => of([])))
      .subscribe({
        next: (versions) => {
          this.currentTemplateFile =
            versions.find(({ isCurrent }) => isCurrent) ?? null;
          this.previousTemplateFileVersions = versions.filter(
            ({ isCurrent }) => !isCurrent
          );
          this._cdr.markForCheck();
        }
      });
  }

  public downloadTemplateFileVersion(templateFileId: number): void {
    const esignTemplateId = this.ESignTemplateForm.controls.id.value;

    if (!esignTemplateId || this.isDownloadingTemplateFile) {
      return;
    }

    this.isDownloadingTemplateFile = true;

    from(
      esignTemplatesService.getTemplateFileVersionPresignedUrl({
        pathParams: { esignTemplateId, templateFileId }
      })
    )
      .pipe(
        finalize(() => {
          this.isDownloadingTemplateFile = false;
          this._cdr.markForCheck();
        })
      )
      .subscribe({
        next: ({ presignedUrl, filename }) => {
          saveAs(presignedUrl, filename);
        },
        error: () => {
          this.ToastService.addErrorToast(
            'Error',
            "Couldn't download the template file. Try again."
          );
        }
      });
  }

  /**
   * How many signatures saving this mapping would invalidate. Read before the
   * admin commits, because the change has no undo.
   */
  public loadResignImpact(): void {
    const esignTemplateId = this.ESignTemplateForm.controls.id.value;

    if (!esignTemplateId) {
      this.resignImpactCount = null;
      return;
    }

    from(
      esignTemplatesService.countSignaturesRequiringResign({
        pathParams: { esignTemplateId },
        options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
      })
    ).subscribe({
      next: ({ signedDocumentCount }) => {
        this.resignImpactCount = signedDocumentCount;
        this._cdr.markForCheck();
      }
    });
  }

  removeFile({ index }: { index: number }) {
    this.Files.splice(index, 1)[0];
    this.ESignTemplateForm.controls.TemplateFile?.reset();
  }

  close(reason?: string) {
    this.activeModal.dismiss(reason);
  }

  save() {
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.ESignTemplateForm
    );

    if (errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save. There are errors in the form.',
        { returnError: true }
      );
    }

    const {
      TemplateFile,
      fields: _fields,
      ...restOfForm
    } = this.ESignTemplateForm.getRawValue();

    // The VIVE branch attaches the TemplateFile group as soon as the provider is
    // picked, so an edit that changes only details would post it empty. The
    // backend validates the nested group whenever it is present -- filename,
    // filetype and filecontents are all required -- so an empty one is a 400,
    // not an omission. Send it only when a file was actually chosen.
    const ESignTemplate = this.Files.length
      ? { ...restOfForm, TemplateFile }
      : restOfForm;

    // `fields` is OMITTED unless there's a mapping to write, and that
    // distinction is load-bearing: the backend reads an omitted key as "leave
    // the mapping alone" and an empty array as "clear it". Sending [] from a
    // modal that never discovered anything would un-map a template on an edit
    // that had nothing to do with its fields. Sending an untouched table on a
    // template with no mapping would create one. See `willSendFields`.
    if (!this.willSendFields) {
      return this.activeModal.close(ESignTemplate);
    }

    this.activeModal.close({
      ...ESignTemplate,
      fields: this.submittableFields()
    });
  }

  /**
   * The mapping rows as the backend takes them.
   *
   * `isInPdf`, `wasSuggested`, `options` and `takesNoValue` are view state and
   * never payload properties. A mapping row for a field the PDF lost is still a
   * row the client wants kept, so it is sent like any other. The flags only
   * drive how a row is drawn, and the option set belongs to the file, which the
   * backend re-reads rather than trusting a client to report.
   */
  private submittableFields(): SubmittableField[] {
    return this.fields
      .getRawValue()
      .map(
        ({
          isInPdf: _isInPdf,
          wasSuggested: _wasSuggested,
          options: _options,
          takesNoValue: _takesNoValue,
          optionLabels,
          ...field
        }) => ({
          ...field,
          optionLabels: this.submittableOptionLabels(optionLabels)
        })
      );
  }

  /**
   * The option labels worth storing, or null when there are none.
   *
   * A label box left empty must arrive as a MISSING KEY, never as `''`. The
   * backend resolves a vendor-facing name with `labels[value] ?? value`, and
   * `'' ?? value` is `''` — so an empty string does not fall back to the export
   * value, it replaces it with nothing. The vendor would be given a radio
   * button with no text beside it on a document they are signing, which is
   * strictly worse than reading `Choice1`.
   *
   * Empty collapses to null rather than `{}` so a text field does not persist
   * an empty object into a column whose whole meaning is "this field has named
   * choices".
   */
  private submittableOptionLabels(
    optionLabels: Record<string, string>
  ): Record<string, string> | null {
    const labelled = Object.entries(optionLabels)
      .map(([option, label]): [string, string] => [option, label.trim()])
      .filter(([, label]) => label.length > 0);

    return labelled.length > 0 ? Object.fromEntries(labelled) : null;
  }

  ngOnInit(): void {
    this.ESignTemplateForm.valueChanges.subscribe({
      next: (changes) => {
        if (this.ESignTemplate) {
          Object.assign(this.ESignTemplate, changes);
          this._cdr.markForCheck();
        }
      }
    });

    forkJoin([
      this.CompanyService.getCompanies(
        this.UtilityService.stringifyObjectsInQueryObject({
          attributes: ['id', 'legalName']
        })
      ),
      this.ESignService.getESignProviders()
    ]).subscribe({
      next: ([Companies, ESignProviders]) => {
        this.Companies = Companies;
        this.ESignProviders = ESignProviders;

        if (this.ESignTemplate) {
          // `fields` is deliberately not patched. The FormArray is empty until
          // `loadAcroFormFields` builds it a row at a time, and `patchValue`
          // only reaches controls that already exist — so this was always a
          // no-op, it just could not be seen until the row shape stopped
          // matching the wire shape. Every mapping row comes from the read
          // below, which clears the array first.
          const { fields: _fields, ...templateDetails } = this.ESignTemplate;

          this.ESignTemplateForm.patchValue(templateDetails);
          this.onESignProviderChange();

          // Only VIVE templates are stamped by this app, so only they have a
          // mapping to read. A DocuSign template is filled by DocuSign.
          if (this.isViveProvider) {
            this.loadAcroFormFields();
            this.loadResignImpact();
            this.loadTemplateFileVersions();
          }
        } else {
          this.ESignTemplate = new ESignTemplate({});
          this.ESignTemplateForm.patchValue({
            templateType: 'Vendor Agreement & W9'
          });
        }

        this._cdr.markForCheck();
      }
    });

    this._cdr.markForCheck();
  }
}
